import { z } from "zod";

export const FeedbackCauseSchema = z.enum(["knowledge", "intent", "page", "model", "product", "other"]);
export type FeedbackCause = z.infer<typeof FeedbackCauseSchema>;

export const CaseFeedbackRequestSchema = z.object({
  resolution: z.enum(["solved", "unsolved"]),
  cause: FeedbackCauseSchema.optional(),
  feedback: z.string().trim().max(1000).optional(),
});
export type CaseFeedbackRequest = z.infer<typeof CaseFeedbackRequestSchema>;

export const ReportPageConflictRequestSchema = z.object({
  pageFact: z.string().trim().min(2).max(2000),
  note: z.string().trim().max(1000).optional(),
});
export type ReportPageConflictRequest = z.infer<typeof ReportPageConflictRequestSchema>;

export const KnowledgeSourceSchema = z.object({
  title: z.string().min(1).max(240),
  url: z.string().url().max(1200),
  type: z.enum(["official", "approved_case"]),
});
export type KnowledgeSource = z.infer<typeof KnowledgeSourceSchema>;

export const KnowledgeItemStatusSchema = z.enum(["draft", "in_review", "approved", "retired"]);
export type KnowledgeItemStatus = z.infer<typeof KnowledgeItemStatusSchema>;

export const KnowledgeItemSchema = z.object({
  id: z.string().uuid(),
  title: z.string().min(1).max(240),
  answer: z.string().min(1).max(12000),
  platform: z.enum(["TikTok Shop", "Temu", "Shopee", "1688", "通用"]),
  site: z.string().min(1).max(240),
  scope: z.string().min(1).max(500),
  sources: z.array(KnowledgeSourceSchema).min(1).max(12),
  status: KnowledgeItemStatusSchema,
  sanitized: z.boolean(),
  reviewedAt: z.string().nullable(),
  nextReviewAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type KnowledgeItem = z.infer<typeof KnowledgeItemSchema>;

export const CreateKnowledgeItemRequestSchema = z.object({
  title: z.string().trim().min(1).max(240),
  answer: z.string().trim().min(1).max(12000),
  platform: KnowledgeItemSchema.shape.platform,
  site: z.string().trim().min(1).max(240),
  scope: z.string().trim().min(1).max(500),
  sources: z.array(KnowledgeSourceSchema).min(1).max(12),
  nextReviewAt: z.string().datetime().optional(),
  reviewItemId: z.string().uuid().optional(),
});
export type CreateKnowledgeItemRequest = z.infer<typeof CreateKnowledgeItemRequestSchema>;

export const ReviewKnowledgeItemRequestSchema = z.object({
  decision: z.enum(["approve", "request_changes", "retire"]),
  note: z.string().trim().min(2).max(1000),
  nextReviewAt: z.string().datetime().optional(),
});
export type ReviewKnowledgeItemRequest = z.infer<typeof ReviewKnowledgeItemRequestSchema>;

export const KnowledgeReviewItemSchema = z.object({
  id: z.string().uuid(),
  kind: z.enum(["negative_feedback", "page_conflict", "human_candidate"]),
  status: z.enum(["open", "in_review", "resolved", "rejected"]),
  cause: FeedbackCauseSchema.nullable(),
  caseId: z.string().nullable(),
  snapshot: z.record(z.string(), z.unknown()),
  resolutionNote: z.string().nullable(),
  knowledgeItemId: z.string().uuid().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type KnowledgeReviewItem = z.infer<typeof KnowledgeReviewItemSchema>;

export const ResolveKnowledgeReviewRequestSchema = z.object({
  status: z.enum(["in_review", "resolved", "rejected"]),
  cause: FeedbackCauseSchema.optional(),
  resolutionNote: z.string().trim().min(2).max(1000),
  knowledgeItemId: z.string().uuid().optional(),
});
export type ResolveKnowledgeReviewRequest = z.infer<typeof ResolveKnowledgeReviewRequestSchema>;
